import React, { useState } from 'react';

const JOBS = [
  {
    dept: 'Bioinformatics',
    title: 'Senior Bioinformatician',
    type: 'Full-time · Bozeman, MT',
    desc: 'Develop and maintain methylation analysis pipelines for the LENA platform. Experience with R/Python, Illumina array data (450K, EPIC), and statistical modeling required. Familiarity with survival analysis and high-dimensional genomic data is a strong plus.',
    reqs: ['PhD or MS in Bioinformatics, Computational Biology, or related field', '3+ years with methylation array data (minfi, ChAMP, or equivalent)', 'Proficiency in R and Python', 'Experience with cloud computing (AWS or Azure preferred)'],
  },
  {
    dept: 'Commercial',
    title: 'Oncology Field Sales Representative',
    type: 'Full-time · Remote (Western US territory)',
    desc: 'Drive commercial adoption of LENA Q51® and LENA S1® among hematologists, oncologists, and clinical laboratory directors. Build and manage a pipeline of oncology and hematology accounts across the western United States.',
    reqs: ['3+ years oncology or diagnostics field sales', 'Existing relationships with hematology/oncology practices preferred', 'Understanding of LDT/CLIA commercial models', 'Willingness to travel 50%+'],
  },
  {
    dept: 'Regulatory Affairs',
    title: 'Regulatory Affairs Specialist',
    type: 'Full-time · Bozeman, MT or Remote',
    desc: 'Support the development and execution of Alercell\'s regulatory strategy — from LDT operations through De Novo submission for the LENA qMETHYL Series™. Interface with FDA and manage regulatory documentation.',
    reqs: ['3+ years IVD regulatory affairs experience', 'Experience with FDA De Novo or 510(k) submissions preferred', 'Familiarity with CLIA/CAP laboratory regulations', 'RAC certification preferred'],
  },
  {
    dept: 'Laboratory Operations',
    title: 'Clinical Laboratory Scientist',
    type: 'Full-time · Bozeman, MT',
    desc: 'Operate and maintain the LenaDx clinical laboratory. Perform methylation array processing (Illumina 450K/EPIC), QC, and result reporting. Ensure CLIA compliance across all laboratory activities.',
    reqs: ['ASCP-certified Medical Laboratory Scientist (MLS or MLS(ASCP))', 'Experience with Illumina methylation arrays preferred', 'CLIA compliance and laboratory QC experience', 'Attention to detail; ability to work independently'],
  },
];

export default function Career() {
  const [selectedRole, setSelectedRole] = useState('');
  const [form, setForm] = useState({ name: '', email: '', role: '', linkedin: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [expandedJob, setExpandedJob] = useState(null);

  const handleApply = (title) => {
    setSelectedRole(title);
    setForm({ ...form, role: title });
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '34b8690d-595c-4a27-8bfc-5180af547bfd',
          subject: `Career Application: ${form.role || 'General Application'} — ${form.name}`,
          from_name: form.name,
          name: form.name,
          email: form.email,
          role: form.role,
          linkedin: form.linkedin || 'Not provided',
          message: form.message,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setError('Submission failed. Please email your application directly to info@alercell.com.');
      }
    } catch (err) {
      setError('Network error. Please email your application directly to info@alercell.com.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      {/* Hero */}
      <section className="page-hero">
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <span className="eyebrow">Join Alercell</span>
          <h1 className="hero-headline" style={{ maxWidth: 780, marginBottom: 24 }}>
            Work on the science that detects cancer before it exists clinically.
          </h1>
          <p className="body-lg" style={{ maxWidth: 640 }}>
            Alercell, Inc. is a small, focused team of scientists and builders working at the intersection of AI, epigenetics, and oncology. Headquartered in Bozeman, Montana — and we think that's an advantage, not a compromise.
          </p>
        </div>
      </section>

      {/* Why Bozeman */}
      <section id="bozeman" className="section section-surface">
        <div className="container">
          <span className="eyebrow">Why Bozeman</span>
          <h2 className="section-headline" style={{ marginBottom: 24 }}>World-class science. Mountain town quality of life. No coastal cost structure.</h2>
          <div className="cyan-line" />
          <div className="stat-grid">
            {[
              { icon: '🏔️', title: 'Quality of Life', body: 'Bozeman offers hiking, skiing, fly fishing, and four seasons of mountain recreation — 15 minutes from the office. A quality of life that coastal biotech hubs cannot match at any salary.' },
              { icon: '🎓', title: 'Montana State University', body: 'World-class computational biology, bioengineering, and chemistry departments within walking distance of our headquarters. A talent ecosystem that lets us recruit exceptional scientists at lower cost structures.' },
              { icon: '💰', title: 'Equity Upside', body: 'Series A stage means real equity value. We compete on upside, mission, and quality of work — not just base salary. Early employees have opportunity to participate in Alercell\'s commercial trajectory.' },
              { icon: '🔬', title: 'Mission-Driven Work', body: 'You will be working on the only validated pre-symptomatic leukemia detection platform in existence. The scientific problems here are unsolved. The clinical stakes are real.' },
            ].map((item, i) => (
              <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28 }}>
                <span style={{ fontSize: 24, display: 'block', marginBottom: 12 }}>{item.icon}</span>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>{item.title}</h3>
                <p className="body-md">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Roles */}
      <section id="open-roles" className="section" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          <span className="eyebrow">Open Positions</span>
          <h2 className="section-headline" style={{ marginBottom: 8 }}>Current openings.</h2>
          <div className="cyan-line" />
          {JOBS.map((job, i) => (
            <div key={i}
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28, marginBottom: 16, transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-cyan-border)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20 }}>
                <div style={{ flex: 1 }}>
                  <span className="job-dept">{job.dept}</span>
                  <div className="job-title">{job.title}</div>
                  <div className="job-meta">{job.type}</div>
                  {expandedJob === i && (
                    <div style={{ marginTop: 20 }}>
                      <p className="body-md" style={{ marginBottom: 16 }}>{job.desc}</p>
                      <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 10 }}>Requirements</p>
                      <ul style={{ paddingLeft: 20 }}>
                        {job.reqs.map((r, ri) => <li key={ri} style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 6, lineHeight: 1.5 }}>{r}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10, flexShrink: 0, alignItems: 'center' }}>
                  <button onClick={() => setExpandedJob(expandedJob === i ? null : i)} className="btn btn-outline" style={{ fontSize: 13, padding: '8px 16px' }}>
                    {expandedJob === i ? 'Less' : 'Details'}
                  </button>
                  <button onClick={() => handleApply(job.title)} className="btn btn-primary" style={{ fontSize: 13, padding: '8px 20px' }}>Apply</button>
                </div>
              </div>
            </div>
          ))}
          <div style={{ background: 'var(--bg-card)', border: '1px dashed var(--border)', borderRadius: 'var(--radius)', padding: 28, textAlign: 'center', marginTop: 8 }}>
            <p className="body-md" style={{ marginBottom: 12 }}>Don't see a role that fits? We're always interested in hearing from exceptional scientists and builders.</p>
           <a href="mailto:info@alercell.com" style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--accent-cyan)', textDecoration: 'none' }}>Send your CV to info@alercell.com →</a>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="section section-surface">
        <div className="container" style={{ maxWidth: 640 }}>
          <span className="eyebrow">Apply</span>
          <h2 className="section-headline" style={{ marginBottom: 12 }}>
            {selectedRole ? `Apply for: ${selectedRole}` : 'Submit your application.'}
          </h2>
          <p className="body-md" style={{ marginBottom: 32 }}>
            We review every submission personally and will be in touch if your background is a strong match.
          </p>

          {submitted ? (
            <div style={{ background: 'var(--accent-cyan-dim)', border: '1px solid var(--accent-cyan-border)', borderRadius: 'var(--radius)', padding: 40, textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Application received.</p>
              <p className="body-md">Thank you for your interest. We review every submission personally and will be in touch if your background is a strong match.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Position *</label>
                <select className="form-select" required value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="">Select a role...</option>
                  {JOBS.map(j => <option key={j.title} value={j.title}>{j.title}</option>)}
                  <option value="General Application">General Application</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">LinkedIn Profile</label>
                <input className="form-input" type="url" placeholder="https://linkedin.com/in/yourprofile" value={form.linkedin} onChange={e => setForm({ ...form, linkedin: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Cover Note *</label>
                <textarea className="form-textarea" required style={{ minHeight: 140 }} placeholder="Tell us about your background and why you want to work on early cancer detection at Alercell." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
              </div>

              {error && (
                <div style={{ background: 'rgba(255,80,80,0.08)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
                  <p style={{ fontSize: 13, color: '#ff6b6b', margin: 0 }}>{error}</p>
                </div>
              )}

              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
                Please email your CV to <a href="mailto:info@alercell.com" style={{ color: 'var(--accent-cyan)' }}>info@alercell.com</a> alongside submitting this form.
              </p>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
                style={{ width: '100%', justifyContent: 'center', padding: '14px', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
